import { SOCKET_MESSAGE_TYPES } from "../constants/OtherKeys.jsx";
import { WEBSOCKET_ACTION_KEY } from "../../env.jsx";
import { sendCustomMessageOnSentry } from "../../sentryIntegration.jsx";

class WebsocketService {
  constructor(url) {
    this.url = url;
    this.ws = null;
    this.pingIntervalTimeoutClear = null;
    this.heartbeatInterval = 1000 * 60; // Time between pings (Ping in 60 seconds)
    this.reconnectDelay = 4000; // Time between pings
    this.reconnectionTimeoutClear = null
    this.isAlive = false;
    this.isReconnecting = false; // To avoid duplicate reconnection attempts
    this.validMessageTypes = new Set(Object.values(SOCKET_MESSAGE_TYPES));

    // Retry tracking
    this.retryCount = 0;
    this.maxRetries = 20;

    // We want to make a cleanup so assigning it separately into `this` context
    this.systemAwakeListener = this.handleSystemWake.bind(this);
    this.networkChangeListener = this.handleNetworkChange.bind(this);
    this.systemSleepListener = this.handleSystemSleep.bind(this);

    window.addEventListener("visibilitychange", this.systemAwakeListener);
    window.addEventListener("online", this.networkChangeListener);
    window.addEventListener("offline", this.systemSleepListener);
  }

  connect() {
    console.log(`Connecting to WebSocket...`);
    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      console.log("WebSocket connection opened.");
      this.isAlive = true; // Mark connection as alive
      this.isReconnecting = false; // Reset reconnection flag
      this.retryCount = 0; // Reset retry count on successful connection
      clearTimeout(this.reconnectionTimeoutClear);
      this.announceConnectionUpdate({ isReconnecting: this.isReconnecting });
      this.startHeartbeat(); // Restart heartbeat after successful connection
    };

    this.ws.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (this.validMessageTypes.has(message[WEBSOCKET_ACTION_KEY])) {
        if (message[WEBSOCKET_ACTION_KEY] === SOCKET_MESSAGE_TYPES.PONG) {
          console.log("Pong received. Connection is alive.");
          this.isAlive = true;
        }
      } else {
        // Log unexpected message types to Sentry
        sendCustomMessageOnSentry({
          messageTitle: "Unexpected WebSocket message type received",
          logType: "warning",
          extraDataBody: {
            messageType: message[WEBSOCKET_ACTION_KEY],
            messageData: message,
            url: this.url
          }
        });
      }
    };

    this.ws.onclose = (event) => {
      this.isReconnecting = false;
      console.warn("WebSocket closed:", event.reason);
      // Log to Sentry if the connection was closed abnormally
      if (!event.wasClean) {
        sendCustomMessageOnSentry({
          messageTitle: "WebSocket connection closed abnormally",
          logType: "warning",
          extraDataBody: {
            reason: event.reason,
            code: event.code,
            wasClean: event.wasClean,
            url: this.url
          }
        });
      }
      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      this.isReconnecting = false;
      console.error("WebSocket error:", error);
      this.scheduleReconnect();
    };

  }

  startHeartbeat() {
    if (this.pingIntervalTimeoutClear) clearInterval(this.pingIntervalTimeoutClear);

    this.pingIntervalTimeoutClear = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        if (!this.isAlive) {
          console.warn("No pong received. Scheduling reconnect...");
          this.scheduleReconnect();
        } else {
          this.isAlive = false; // Reset alive status before sending ping
          console.log("Sending ping...");
          this.send({ [WEBSOCKET_ACTION_KEY]: SOCKET_MESSAGE_TYPES.PING });
        }
      }
    }, this.heartbeatInterval);
  }

  handleSystemSleep() {
    if (!navigator.onLine) {
      this.announceConnectionUpdate({ isReconnecting: true });
    }
  }

  handleSystemWake() {
    if (document.visibilityState === "visible") {
      console.log("System resumed from sleep or hibernate.");
      this.isReconnecting = false;
      this.retryCount = 0;
      this.ensureConnection();
    }
  }

  handleNetworkChange() {
    console.log("Network status changed.");
    if (navigator.onLine) {
      this.isReconnecting = false;
      this.retryCount = 0;
      this.announceConnectionUpdate({ isReconnecting: this.isReconnecting });
      this.ensureConnection();
    }
  }

  ensureConnection() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.log("Ensuring WebSocket connection after system wake or network change.");
      this.scheduleReconnect();
    }
  }

  scheduleReconnect() {
    if (this.isReconnecting) {
      console.log("Reconnect already scheduled. Skipping duplicate attempt.");
      return;
    }

    // Check if we've exceeded max retries
    if (this.retryCount >= this.maxRetries) {
      this.announceConnectionUpdate({
        isReconnecting: false,
        maxRetriesReached: true,
      });
      return;
    }

    this.retryCount = this.retryCount + 1;
    console.log(`Scheduling WebSocket reconnect... (Attempt ${this.retryCount}/${this.maxRetries})`);
    this.isReconnecting = true; // Set flag to avoid duplicate reconnects
    this.announceConnectionUpdate({ isReconnecting: this.isReconnecting });
    this.cleanup(); // Ensure cleanup before reconnecting

    this.reconnectionTimeoutClear = setTimeout(() => {
      console.log(`Attempting to reconnect... (Attempt ${this.retryCount}/${this.maxRetries})`);
      this.connect();
    }, this.reconnectDelay); // Delay before reconnecting
  }

  cleanup() {
    if (this.pingIntervalTimeoutClear) clearInterval(this.pingIntervalTimeoutClear); // Stop heartbeat
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close(); // Ensure the connection is fully closed
    }
    this.ws = null;
    this.isAlive = false;
    this.announceConnectionUpdate({ isReconnecting: this.isReconnecting });
  }

  destroy() {
    console.log("Destroying WebSocket and cleaning up resources.");
    this.cleanup();
    window.removeEventListener("visibilitychange", this.systemAwakeListener);
    window.removeEventListener("online", this.networkChangeListener);
    window.removeEventListener("offline", this.systemSleepListener);
    // Todo: if listeners are removed then make sure they are attached again when connect is called otherwise they'll never be attached
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn("Cannot send message. WebSocket is not open.");
    }
  }

  announceConnectionUpdate({ _isReconnecting }) {
    // implemented by the inherited class
  }
}

export default WebsocketService;
